import {
  IPaymentRequisitionActions,
  IPaymentRequisitionApproverActionPayload,
  IPaymentRequisitionAPSpecialistActionPayload,
  IPaymentRequisitionFetchActionPayload,
  IPaymentRequisitionStateActionPayload,
  PaymentRequisitionContext,
  IPaymentRequisitionAttachmentActionPayload,
  IPaymentRequisitionCommentFetchActionPayload,
  IPaymentRequisitionCommentUpdateActionPayload,
  IPaymentRequisitionOwnerActionPayload,
  IPaymentRequisitionOwnerMultipleUpdateActionPayload,
  IPaymentRequisitionCommentAttachmentAddActionPayload,
  IPaymentRequisitionCommentAttachmentRemoveActionPayload,
  IPaymentRequisitionLineUpdateActionPayload
} from './actions.types';
import {
  IPaymentRequisitionCommentData,
  IPaymentRequisitionData
} from '@/models';
import { ITransition, Transition } from '@/models/transition';
import {
  IPaymentRequisitionPaginatedListData,
  IPaymentRequisitionExportFilterParams
} from '@/services/payment-requisition';
import { IUserData } from '@vue-altoleap-libraries/vue-altoleap-accounts-lib/types/model/userData.types';

export const actions: IPaymentRequisitionActions = {
  fetchPaymentRequisitions(
    context: PaymentRequisitionContext,
    payload: IPaymentRequisitionFetchActionPayload
  ): Promise<void> {
    const promise = this.$services.paymentRequisition.fetchPaymentRequisitions(
      payload
    );
    context.commit('SET_LAST_PROMISE', promise);
    return promise.then(
      (paymentRequisitions: IPaymentRequisitionPaginatedListData) => {
        if (promise == context.state.lastPromise) {
          context.commit(
            'SET_PAYMENT_REQUISITIONS',
            paymentRequisitions.results
          );
          context.commit('SET_COUNT', paymentRequisitions.count);

          context.commit('SET_LAST_PROMISE', null);
        }
      }
    );
  },

  fetchPaymentRequisition(
    context: PaymentRequisitionContext,
    payload: number
  ): Promise<void> {
    return this.$services.paymentRequisition
      .fetchPaymentRequisition(payload)
      .then((paymentRequisition: IPaymentRequisitionData) => {
        const index = context.state.paymentRequisitions.findIndex(
          (v: IPaymentRequisitionData) => v.id === paymentRequisition.id
        );
        if (index === -1) {
          context.commit('ADD_PAYMENT_REQUISITION', paymentRequisition);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_PAYMENT_REQUISITION', paymentRequisition);
        }
      });
  },

  createPaymentRequisition(
    context: PaymentRequisitionContext,
    payload: IPaymentRequisitionData
  ): Promise<void> {
    return this.$services.paymentRequisition
      .createPaymentRequisition(payload)
      .then((paymentRequisition: IPaymentRequisitionData) => {
        context.commit('ADD_PAYMENT_REQUISITION', paymentRequisition);
        context.commit(
          'SET_LAST_CREATED_PAYMENT_REQUISITION_ID',
          paymentRequisition.id!
        );
        context.commit('INCREMENT_COUNT');
      });
  },

  updatePaymentRequisition(
    context: PaymentRequisitionContext,
    payload: IPaymentRequisitionData
  ): Promise<void> {
    return this.$services.paymentRequisition
      .updatePaymentRequisition(payload)
      .then((paymentRequisition: IPaymentRequisitionData) => {
        const index = context.state.paymentRequisitions.findIndex(
          (v: IPaymentRequisitionData) => v.id === paymentRequisition.id
        );
        if (index === -1) {
          context.commit('ADD_PAYMENT_REQUISITION', paymentRequisition);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_PAYMENT_REQUISITION', paymentRequisition);
        }
      });
  },

  deletePaymentRequisition(
    context: PaymentRequisitionContext,
    payload: number
  ): Promise<void> {
    return this.$services.paymentRequisition
      .deletePaymentRequisition(payload)
      .then(() => {
        context.commit('REMOVE_PAYMENT_REQUISITION', payload);
        context.commit('DECREMENT_COUNT');
      });
  },

  updatePaymentRequisitionLine(
    context: PaymentRequisitionContext,
    payload: IPaymentRequisitionLineUpdateActionPayload
  ): void {
    return context.commit('UPDATE_PAYMENT_REQUISITION_LINE', {
      id: payload.paymentRequisitionId,
      line: payload.line
    });
  },

  deletePaymentRequisitionAttachment(
    context: PaymentRequisitionContext,
    payload: IPaymentRequisitionAttachmentActionPayload
  ): Promise<void> {
    return this.$services.paymentRequisition.deletePaymentRequisitionAttachment(
      payload.paymentRequisitionId,
      payload.attachmentId
    );
  },

  updatePaymentRequisitionState(
    context: PaymentRequisitionContext,
    payload: IPaymentRequisitionStateActionPayload
  ): Promise<void> {
    return this.$services.paymentRequisition
      .updatePaymentRequisitionState(
        payload.paymentRequisitionId,
        payload.state,
        payload.assignee,
        payload.comment
      )
      .then((paymentRequisition: IPaymentRequisitionData) => {
        const index = context.state.paymentRequisitions.findIndex(
          (v: IPaymentRequisitionData) => v.id === paymentRequisition.id
        );
        if (index === -1) {
          context.commit('ADD_PAYMENT_REQUISITION', paymentRequisition);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_PAYMENT_REQUISITION', paymentRequisition);
        }
      });
  },

  fetchRequisitionAPSpecialists(
    context: PaymentRequisitionContext,
    payload: number
  ): Promise<void> {
    return this.$services.paymentRequisition
      .fetchRequisitionAPSpecialists(payload)
      .then((apSpecialists: IUserData[]) => {
        context.commit('SET_PAYMENT_REQUISITION_AP_SPECIALISTS', apSpecialists);
      });
  },

  assignRequisitionAPSpecialist(
    context: PaymentRequisitionContext,
    payload: IPaymentRequisitionAPSpecialistActionPayload
  ): Promise<void> {
    return this.$services.paymentRequisition
      .assignRequisitionAPSpecialist(
        payload.paymentRequisitionId,
        payload.apSpecialist
      )
      .then((paymentRequisition: IPaymentRequisitionData) => {
        const index = context.state.paymentRequisitions.findIndex(
          (v: IPaymentRequisitionData) => v.id === paymentRequisition.id
        );
        if (index === -1) {
          context.commit('ADD_PAYMENT_REQUISITION', paymentRequisition);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_PAYMENT_REQUISITION', paymentRequisition);
        }
      });
  },

  fetchPaymentRequisitionApprovers(
    context: PaymentRequisitionContext,
    payload: number
  ): Promise<void> {
    return this.$services.paymentRequisition
      .fetchPaymentRequisitionApprovers(payload)
      .then((approvers: IUserData[]) => {
        context.commit('SET_PAYMENT_REQUISITION_APPROVERS', approvers);
      });
  },

  assignPaymentRequisitionApprover(
    context: PaymentRequisitionContext,
    payload: IPaymentRequisitionApproverActionPayload
  ): Promise<void> {
    return this.$services.paymentRequisition
      .assignPaymentRequisitionApprover(
        payload.paymentRequisitionId,
        payload.approver
      )
      .then((paymentRequisition: IPaymentRequisitionData) => {
        const index = context.state.paymentRequisitions.findIndex(
          (v: IPaymentRequisitionData) => v.id === paymentRequisition.id
        );
        if (index === -1) {
          context.commit('ADD_PAYMENT_REQUISITION', paymentRequisition);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_PAYMENT_REQUISITION', paymentRequisition);
        }
      });
  },

  updatePaymentRequisitionOwner(
    context: PaymentRequisitionContext,
    payload: IPaymentRequisitionOwnerActionPayload
  ): Promise<void> {
    return this.$services.paymentRequisition
      .updatePaymentRequisitionOwner(
        payload.paymentRequisitionId,
        payload.owner
      )
      .then((paymentRequisition: IPaymentRequisitionData) => {
        const index = context.state.paymentRequisitions.findIndex(
          (v: IPaymentRequisitionData) => v.id === paymentRequisition.id
        );
        if (index === -1) {
          context.commit('ADD_PAYMENT_REQUISITION', paymentRequisition);
          context.commit('INCREMENT_COUNT');
        } else {
          context.commit('UPDATE_PAYMENT_REQUISITION', paymentRequisition);
        }
      });
  },

  fetchPaymentRequisitionActivities(
    context: PaymentRequisitionContext,
    payload: number
  ): Promise<void> {
    return this.$services.paymentRequisition
      .fetchPaymentRequisitionActivities(payload)
      .then((activities: Transition[]) => {
        context.commit('SET_PAYMENT_REQUISITION_ACTIVITIES', activities);
      });
  },

  fetchPaymentRequisitionComments(
    context: PaymentRequisitionContext,
    payload: number
  ): Promise<void> {
    return this.$services.paymentRequisition
      .fetchPaymentRequisitionComments(payload)
      .then((comments: ITransition[]) => {
        context.commit('SET_PAYMENT_REQUISITION_COMMENTS', comments);
      });
  },

  fetchPaymentRequisitionComment(
    context: PaymentRequisitionContext,
    payload: IPaymentRequisitionCommentFetchActionPayload
  ): Promise<void> {
    return this.$services.paymentRequisition
      .fetchPaymentRequisitionComment(payload.requisitionId, payload.commentId)
      .then((comment: IPaymentRequisitionData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === comment.id
        );
        if (index === -1) {
          context.commit('ADD_PAYMENT_REQUISITION_COMMENT', comment);
        } else {
          context.commit('UPDATE_PAYMENT_REQUISITION_COMMENT', comment);
        }
      });
  },

  createPaymentRequisitionComment(
    context: PaymentRequisitionContext,
    payload: IPaymentRequisitionCommentUpdateActionPayload
  ): Promise<void> {
    return this.$services.paymentRequisition
      .createPaymentRequisitionComment(payload.requisitionId, payload.comment)
      .then((comment: IPaymentRequisitionData) => {
        context.commit(
          'SET_LAST_CREATED_PAYMENT_REQUISITION_COMMENT_ID',
          comment.id
        );
        context.commit('ADD_PAYMENT_REQUISITION_COMMENT', comment);
      });
  },
  updatePaymentRequisitionComment(
    context: PaymentRequisitionContext,
    payload: IPaymentRequisitionCommentUpdateActionPayload
  ): Promise<void> {
    return this.$services.paymentRequisition
      .updatePaymentRequisitionComment(payload.requisitionId, payload.comment)
      .then((comment: IPaymentRequisitionData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === comment.id
        );
        if (index === -1) {
          context.commit('ADD_PAYMENT_REQUISITION_COMMENT', comment);
        } else {
          context.commit('UPDATE_PAYMENT_REQUISITION_COMMENT', comment);
        }
      });
  },
  deletePaymentRequisitionComment(
    context: PaymentRequisitionContext,
    payload: IPaymentRequisitionCommentFetchActionPayload
  ): Promise<void> {
    return this.$services.paymentRequisition
      .deletePaymentRequisitionComment(payload.requisitionId, payload.commentId)
      .then(() => {
        context.commit('REMOVE_PAYMENT_REQUISITION_COMMENT', payload.commentId);
      });
  },

  addPaymentRequisitionCommentAttachments(
    context: PaymentRequisitionContext,
    payload: IPaymentRequisitionCommentAttachmentAddActionPayload
  ): Promise<void> {
    return this.$services.paymentRequisition
      .addPaymentRequisitionCommentAttachments(
        payload.requisitionId,
        payload.commentId,
        payload.attachments
      )
      .then((comment: IPaymentRequisitionCommentData) => {
        const index = context.state.comments.findIndex(
          comment => comment.id === payload.commentId
        );
        if (index === -1) {
          context.commit('ADD_PAYMENT_REQUISITION_COMMENT', comment);
        } else {
          context.commit('UPDATE_PAYMENT_REQUISITION_COMMENT', comment);
        }
      });
  },

  removePaymentRequisitionCommentAttachment(
    context: PaymentRequisitionContext,
    payload: IPaymentRequisitionCommentAttachmentRemoveActionPayload
  ): Promise<void> {
    return this.$services.paymentRequisition
      .removePaymentRequisitionCommentAttachment(
        payload.requisitionId,
        payload.commentId,
        payload.attachmentId
      )
      .then(() => {
        const index = context.state.comments.findIndex(
          comment => comment.id === payload.commentId
        );

        if (index !== -1) {
          const comment = context.state.comments[index];

          const attachmentIndex = comment.attachments!.findIndex(
            attachment => attachment.id === payload.attachmentId
          );

          comment.attachments!.splice(attachmentIndex, 1);

          context.commit('UPDATE_PAYMENT_REQUISITION_COMMENT', comment);
        }
      });
  },

  //export
  exportPaymentRequisitionPdf(
    _context: PaymentRequisitionContext,
    payload: number
  ): Promise<Blob> {
    return this.$services.paymentRequisition.exportPaymentRequisitionPdf(
      payload
    );
  },

  exportPaymentRequisitions(
    _context: PaymentRequisitionContext,
    payload?: IPaymentRequisitionExportFilterParams
  ): Promise<Blob> {
    return this.$services.paymentRequisition.exportPaymentRequisitions(payload);
  },

  updateMultiplePaymentRequisitionOwner(
    _context: PaymentRequisitionContext,
    payload: IPaymentRequisitionOwnerMultipleUpdateActionPayload
  ): Promise<void> {
    return this.$services.paymentRequisition.updateMultiplePaymentRequisitionOwner(
      payload.owner,
      payload.requisitions
    );
  },

  fetchPaymentRequisitionCommentsMentionableUsers(
    context: PaymentRequisitionContext,
    payload: number
  ): Promise<void> {
    return this.$services.paymentRequisition
      .fetchPaymentRequisitionCommentsMentionableUsers(payload)
      .then((mentionableUsers: IUserData[]) => {
        context.commit('SET_MENTIONABLE_USERS', mentionableUsers);
      });
  }
};
